import React, { useMemo, useCallback, useContext } from 'react';
import { DatePicker } from 'antd';
import type { RangePickerProps as AntRangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { useFieldValidation } from '../../base/useFieldValidation';
import { mergeStoreProps } from '../../base/storePropsUtils';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import { resolveDatePickerValueFormat } from '../../utils/resolveDatePickerValueFormat';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';
import { DataSetInheritContext } from '../../base/DataSetInheritContext';

const { RangePicker } = DatePicker;

export interface AndRangePickerProps extends Omit<AntRangePickerProps, 'onChange' | 'value' | 'id'>, BaseComponentProps, DataSetConfig, ValidationDisplayConfig {
  changeAction?: string;
  valueFormat?: string;
  rules?: any[];
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  onChange?: (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => void;
  value?: [string, string] | [Dayjs, Dayjs];
}

export const AndRangePicker: React.FC<AndRangePickerProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  changeAction,
  valueFormat: valueFormatProp,
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  onChange,
  value: propValue,
  defaultValue,
  ...restProps
}) => {
  const ctx = useContext(DataSetInheritContext);
  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );
  const storeProps = useAndStore((s) =>
    sessionId && id ? s.propsStore[`${sessionId}_props`]?.[id] ?? null : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && dataSetField ? s.getDataSet(dataSetKey, dataSetField) : undefined
  );

  const { validate, help } = useFieldValidation({
    rules,
    dataSet,
    dataSetField,
    sessionId,
    id,
    develop,
  });

  const mergedProps = useMemo(
    () => mergeStoreProps(restProps as Record<string, any>, storeProps ?? undefined),
    [restProps, storeProps]
  );

  const displayFormat =
    typeof mergedProps.format === 'string' ? mergedProps.format : undefined;

  const effectiveValueFormat = useMemo(
    () =>
      resolveDatePickerValueFormat({
        valueFormat: valueFormatProp,
        format: displayFormat,
        showTime: mergedProps.showTime === true,
      }),
    [valueFormatProp, displayFormat, mergedProps.showTime],
  );

  const parseRangeValue = useCallback((val: any): [Dayjs, Dayjs] | null => {
    if (!val || !Array.isArray(val) || val.length !== 2) return null;
    const [start, end] = val;
    const startDayjs = dayjs.isDayjs(start) ? start : dayjs(start, effectiveValueFormat);
    const endDayjs = dayjs.isDayjs(end) ? end : dayjs(end, effectiveValueFormat);
    if (startDayjs.isValid() && endDayjs.isValid()) return [startDayjs, endDayjs];
    return null;
  }, [effectiveValueFormat]);

  const displayValue = useMemo(() => {
    const rawValue = storeValue ?? storeProps?.value ?? propValue ?? defaultValue;
    return parseRangeValue(rawValue);
  }, [storeValue, storeProps?.value, propValue, defaultValue, parseRangeValue]);

  const rangePickerDomProps = useMemo(
    () => prepareAntdDomProps(mergedProps as Record<string, unknown>),
    [mergedProps],
  );

  const handleChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => {
      if (onChange) {
        onChange(dates, dateStrings);
        return;
      }
      const persistValue =
        dates?.[0]?.isValid() && dates?.[1]?.isValid()
          ? [
              dates[0]!.format(effectiveValueFormat),
              dates[1]!.format(effectiveValueFormat),
            ]
          : null;
      if (dataSetKey && dataSetField) setDataSet(dataSetKey, dataSetField, persistValue);
      if (validateOnChange) validate(persistValue);
      if (changeAction && sessionId) {
        const { actionName, arg } = parseChangeAction(changeAction, persistValue, {
          id,
          dataSet,
          dataSetField,
        }, ctx);
        addCommand(sessionId, createCommand(actionName, arg));
      }
    },
    [
      onChange,
      dataSetKey,
      dataSetField,
      setDataSet,
      validateOnChange,
      validate,
      changeAction,
      sessionId,
      id,
      addCommand,
      ctx,
      effectiveValueFormat,
    ]
  );

  const handleBlur = useCallback(() => {
    if (validateOnBlur) {
      validate(
        displayValue
          ? [
              displayValue[0]?.format(effectiveValueFormat),
              displayValue[1]?.format(effectiveValueFormat),
            ]
          : null,
      );
    }
  }, [validateOnBlur, validate, displayValue, effectiveValueFormat]);

  return (
    <div onBlur={handleBlur}>
      <RangePicker
        {...rangePickerDomProps}
        disabled={develop ? false : (rangePickerDomProps.disabled as boolean | undefined)}
        open={develop ? false : undefined}
        value={displayValue}
        onChange={handleChange}
        style={{ width: '100%', ...(rangePickerDomProps.style as React.CSSProperties) }}
      />
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndRangePicker;
